import React from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Header,
  Text,
  Label,
  PasswordInput,
  FieldGroup,
  Link,
  Loader,
  Fields,
  Base,
} from '@pet-notify/ui'

import { useUpdatePassword } from 'hooks/me'
import useNotification from 'hooks/useNotification'
import useDocumentTitle from 'hooks/document-title'
import type { UpdatePasswordInput } from 'services/me'
import ErrorMessage from 'components/ErrorMessage'

const schema = yup.object().shape({
  newPassword: yup.string().required('Password is required'),
  confirmNewPassword: yup
    .string()
    .required('Confirm password is required')
    .oneOf([yup.ref('newPassword'), null], 'Must match password'),
})

type FormData = UpdatePasswordInput

function SignupSuccessPage() {
  useDocumentTitle('Thank you | Pet Notify')
  const showNotification = useNotification()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  })

  const updatePasswordMutation = useUpdatePassword()

  const onSubmit = handleSubmit((values) => {
    updatePasswordMutation.mutate(values, {
      onSuccess: () => {
        showNotification('Password set')
        window.location.replace('/?activated=true')
      },
    })
  })

  return (
    <>
      <form onSubmit={onSubmit}>
        <Header mb='4'>Welcome to Pet Notify</Header>

        <Text mb='6'>
          Create your password to build your pet&rsquo;s digital
          profile.
        </Text>

        <Fields>
          <FieldGroup>
            <Label htmlFor='newPassword'>Create your password</Label>
            <Text variant='note'>
              Must contain 6 characters including a number.
            </Text>
            <PasswordInput id='newPassword' {...register('newPassword')} />
          </FieldGroup>

          {errors.newPassword && (
            <ErrorMessage error={errors.newPassword.message} />
          )}

          <FieldGroup>
            <Label htmlFor='confirmNewPassword'>Confirm new password</Label>
            <PasswordInput
              id='confirmNewPassword'
              {...register('confirmNewPassword')}
            />
          </FieldGroup>

          {errors.confirmNewPassword && (
            <ErrorMessage error={errors.confirmNewPassword.message} />
          )}

          {updatePasswordMutation.isError && (
            <ErrorMessage error={updatePasswordMutation.error} />
          )}
        </Fields>

        <Base mb='6'>
          {updatePasswordMutation.isLoading ? (
            <Loader />
          ) : (
            <Button type='submit' disabled={updatePasswordMutation.isLoading}>
              Set password
            </Button>
          )}
        </Base>
      </form>

      <Link href='/?activated=true'>
        Skip and we&apos;ll send you an email to set your password later
      </Link>
    </>
  )
}

export default SignupSuccessPage
